import React, { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useNavigate } from 'react-router';
import Navbar from 'components/Navbar';
import Footer from 'components/Footer/Index';
import NoData from 'components/shared/NoData/Index';
import useLocales from 'hooks/useLocales';
import Pagination from '@material-ui/core/Pagination';
import CompanyCard from 'components/CompanyCard/Index';
import { CompanyService } from 'services/companes/companies';
import Autocomplete from 'components/shared/Autocomplete/Index';
import DropDown from 'components/Dropdown';
import { ProfileService } from 'services/profile/profile';
import { JobService } from 'services/jobs/job';
import { JobCitiesResultsType } from 'core/interface/job';
import { companySizeChoices } from 'core/constants/aboutMePopUpConstants';
import { Formik, Form } from 'formik';
import { IIndustry } from 'core/interface/companies';

import './index.scss';
import colors from 'assets/variables/colors';

interface PublicCompany {
  id: number;
  name: string;
  description?: string;
  logo: string | null;
  hero_image?: string;
  industry: IIndustry | null;
  size: number | null;
  location: { display_name: string } | null;
  number_of_jobs: number;
  currency: string;
  slug: string;
  has_career_page?: boolean;
}

interface FilterFormValues {
  industry: string;
  location: string;
  company_size: string;
}

const PublicCompanies: FC = (): JSX.Element => {
  const { t } = useLocales();
  const navigate = useNavigate();
  const { publicCompanies } = useSelector((state: RootState) => state.company);
  const { industry: industries } = useSelector(
    (state: RootState) => state.industries
  );
  const { cities } = useSelector((state: RootState) => state.locations);
  const targetRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState({
    industry: '',
    location: { id: 0, name: '' },
    company_size: ''
  });

  const initialFormValues: FilterFormValues = {
    industry: '',
    location: '',
    company_size: ''
  };

  useEffect(() => {
    // Load initial data
    getIndustries();
    getCities();
  }, []);

  useEffect(() => {
    setOffset((page - 1) * limit);
  }, [page, limit]);

  useEffect(() => {
    getCompanies(limit, offset);
  }, [limit, offset, filters]);

  const getCompanies = async (
    limit?: number,
    offset?: number
  ): Promise<void> => {
    try {
      // TODO: Add filter parameters when API supports filtering
      CompanyService.getPublicCompanies({
        limit,
        offset,
        industry: filters.industry,
        location_id: filters.location.id,
        company_size: filters.company_size
      });
    } catch (error) {
      console.log(error);
    }
  };

  const getIndustries = (query: string = ''): void => {
    ProfileService.getIndustries(query);
  };

  const getCities = (
    q: string = '',
    limit: number = 10,
    append: boolean = false
  ): void => {
    JobService.getLocations(
      {
        limit,
        q
      },
      append
    );
  };

  const handleChangeScroll = (): void => {
    if (targetRef?.current) {
      targetRef.current.scrollIntoView({
        block: 'start'
      });
    }
  };

  useEffect(() => {
    handleChangeScroll();
  }, [targetRef]);

  const handleFilterChange = (field: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));
    setPage(1); // Reset to first page when filters change
  };

  const handleClearIndustry = () => {
    handleFilterChange('industry', '');
  };

  const handleClearLocation = () => {
    handleFilterChange('location', { id: 0, name: '' });
  };

  const handleClearCompanySize = () => {
    handleFilterChange('company_size', '');
  };

  // Prepare the data for the autocomplete components
  const formattedIndustries =
    industries?.map((industry: IIndustry) => ({
      id: industry?.id,
      title: industry?.name,
      name: industry?.name
    })) || [];

  const handleLoadMoreCities = () => {
    const params = { limit: 10, offset: cities.length };
    getCities('', 10, true);
  };

  return (
    <div
      className="publicCompanies"
      style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
    >
      <Navbar />
      <div
        className="public-companies-banner"
        style={{ backgroundColor: colors.tenantColor }}
      >
        <div className="banner-content">
          <h1>{t('Discover Great Companies')}</h1>
          <p>
            {t('Browse through our partner companies and find your next career opportunity')}
          </p>
        </div>
      </div>

      <div className="filters-container">
        <Formik initialValues={initialFormValues} onSubmit={() => {}}>
          <Form className="filters-row">
            <div className="filter-item">
              <span className="filter-label">{t('Industry')}</span>
              <Autocomplete
                fieldName="industry"
                show_arrow={true}
                value={
                  industries?.find(
                    (industry) =>
                      String(industry?.id) === String(filters.industry)
                  )?.name ?? ''
                }
                options={formattedIndustries}
                show_location={false}
                placeholder={t('Select Industry')}
                onChange={(e) => {
                  const activeItem = String(
                    industries?.find((industry) => industry.name === e)?.id ||
                      ''
                  );
                  handleFilterChange('industry', activeItem);
                }}
                onInputChange={(e) => getIndustries(e)}
                values={industries}
                onClear={handleClearIndustry}
              />
            </div>

            <div className="filter-item">
              <span className="filter-label">{t('City')}</span>
              <Autocomplete
                fieldName="location"
                show_arrow={true}
                value={filters.location.name}
                options={
                  cities?.map((e: JobCitiesResultsType) => ({
                    id: e?.id,
                    title: e?.display_name
                  })) || []
                }
                show_location={true}
                placeholder={t('City')}
                onChange={(location: string) => {
                  const activeItem = cities.find(
                    (city: JobCitiesResultsType) =>
                      city.display_name === location
                  );

                  handleFilterChange('location', {
                    id: activeItem?.id,
                    name: activeItem?.display_name
                  });
                }}
                onInputChange={(e) => getCities(e)}
                values={cities}
                loadMore={handleLoadMoreCities}
                onClear={handleClearLocation}
              />
            </div>

            <div className="filter-item">
              <span className="filter-label">{t('Company Size')}</span>
              <DropDown
                fieldName="company_size"
                dropdownClassName="size-dropdown"
                placeholder={t('Select Size')}
                items={companySizeChoices}
                selectedValue={(e) => {
                  handleFilterChange('company_size', e);
                }}
                onClear={handleClearCompanySize}
                showClose={true}
              />
            </div>
          </Form>
        </Formik>
      </div>

      <div className="pcompanies" ref={targetRef}>
        <div className="pcompanies__content">
          {/* <h2>{t('All Companies')}</h2> */}
          <div className="pcompanies__list">
            <div
              className="pcompanies__list__cards"
              style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}
            >
              {!publicCompanies?.results ||
              publicCompanies.results.length === 0 ? (
                <NoData />
              ) : (
                publicCompanies?.results?.map((company: PublicCompany) => (
                  <div
                    key={company.id}
                    className="pcompanies__list__card"
                    style={{ width: 'calc(33.333% - 14px)' }}
                  >
                    <CompanyCard
                      id={company.id}
                      name={company.name || ''}
                      logo={
                        company.logo || '/static/images/recrout-logo-letter.jpg'
                      }
                      heroImage={company.hero_image || ''}
                      industry={company.industry || undefined}
                      size={company.size || undefined}
                      location={company.location?.display_name || ''}
                      number_of_jobs={company.number_of_jobs || 0}
                      currency={company.currency || ''}
                      slug={company.slug || ''}
                      hasCareerPage={company.has_career_page || false}
                    />
                  </div>
                ))
              )}
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 30
              }}
            >
              <Pagination
                className="pagination"
                count={Math.ceil((publicCompanies?.count || 0) / limit)}
                page={page}
                onChange={(_, value) => setPage(value)}
                variant="outlined"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PublicCompanies;
